import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';

import { catchError, finalize, switchMap, throwError } from 'rxjs';

import { LoadingService } from '@sharedServices/loading.service';
import { AuthService } from '@auth/services/auth.service';
import { BYPASS_AUTH_INTERCEPTOR } from '@auth/context/auth.context';

/**
 * An HTTP interceptor that handles authentication.
 *
 * @param req The request to intercept.
 * @param next The next interceptor in the chain.
 * @returns An observable that emits the response.
 */
export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const _loadingService = inject(LoadingService);
  const _authService = inject(AuthService);

  _loadingService.show();

  if (req.context.get(BYPASS_AUTH_INTERCEPTOR)) {
    return next(req);
  }

  const authReq = req.clone({ withCredentials: true });

  return next(authReq).pipe(
    catchError((error: HttpErrorResponse) => {
      if (error.status === 401 && !req.url.includes('refresh-access-token') && !req.url.includes('login')) {
        return _authService.refreshAccessToken().pipe(
          switchMap(() => {
            const cloned = req.clone({ withCredentials: true });
            return next(cloned);
          }),
          catchError(() => {
            _authService.logout();
            return throwError(() => error);
          })
        );
      }

      return throwError(() => error);
    }),
    finalize(() => _loadingService.hide())
  );
};
